import { useMemo } from 'react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { useAnalysisStore } from '../../../shared/lib/store/analysisStore';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler
);

interface QualityTrendChartProps {
    projectId: string;
    fileFilter?: string | null;
}

export function QualityTrendChart({ projectId, fileFilter }: QualityTrendChartProps) {
    const allProjectData = useAnalysisStore(state => state.projectData);
    const singleProjectData = allProjectData[projectId];

    // Build a flat ordered list of results from all projects (for dashboard-aggregated)
    // or just this project's results
    const localResults = useMemo(() => {
        if (projectId === 'dashboard-aggregated') {
            // Collect all results across every project, sorted by time (most recent last)
            const allResults: any[] = Object.values(allProjectData).flatMap(
                (p: any) => p.results ?? []
            );
            // Sort by analyzed_at ascending so trend goes oldest → newest
            allResults.sort((a, b) =>
                (a.analyzed_at ?? '').localeCompare(b.analyzed_at ?? '')
            );
            return allResults;
        }

        if (!singleProjectData) return [];
        const results = fileFilter
            ? singleProjectData.results.filter(r => r.fileName === fileFilter)
            : singleProjectData.results;
        return [...results].reverse(); // oldest first
    }, [allProjectData, singleProjectData, fileFilter, projectId]);

    // Bucket into 4 points
    const bucketSize = Math.max(1, Math.ceil(localResults.length / 4));

    const labels: string[] = [];
    const healthData: number[] = [];
    const issuesData: number[] = [];

    if (localResults.length === 0) {
        // No data — show flat zeros with placeholder labels
        labels.push('Sem 1', 'Sem 2', 'Sem 3', 'Actual');
        healthData.push(0, 0, 0, 0);
        issuesData.push(0, 0, 0, 0);
    } else {
        // Build buckets
        for (let i = 0; i < 4; i++) {
            const start = i * bucketSize;
            const end = Math.min(start + bucketSize, localResults.length);
            const bucket = localResults.slice(start, end);

            if (bucket.length === 0) {
                labels.push(`P${i + 1}`);
                healthData.push(healthData[healthData.length - 1] ?? 0);
                issuesData.push(issuesData[issuesData.length - 1] ?? 0);
                continue;
            }

            const totalIssues = bucket.reduce((sum: number, r: any) => sum + (r.issues?.length ?? 0), 0);
            const avgIssues = totalIssues / bucket.length;
            const health = Math.max(0, Math.min(100, 100 - avgIssues * 5));

            // Label: use analyzed_at of first in bucket if available
            const firstDate = bucket[0]?.analyzed_at;
            const label = firstDate
                ? new Date(firstDate).toLocaleDateString('es', { month: 'short', day: 'numeric' })
                : `P${i + 1}`;

            labels.push(i === 3 ? 'Actual' : label);
            healthData.push(parseFloat(health.toFixed(1)));
            issuesData.push(parseFloat(avgIssues.toFixed(1)));
        }
    }

    const data = {
        labels,
        datasets: [
            {
                label: 'Health Score',
                data: healthData,
                borderColor: 'rgb(34, 197, 94)',
                backgroundColor: 'rgba(34, 197, 94, 0.1)',
                fill: true,
                tension: 0.4,
            },
            {
                label: 'Issues',
                data: issuesData,
                borderColor: 'rgb(234, 179, 8)',
                backgroundColor: 'rgba(234, 179, 8, 0.1)',
                fill: true,
                tension: 0.4,
            }
        ],
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: true,
                position: 'top' as const,
                labels: {
                    font: { family: 'monospace', size: 10 },
                    color: 'rgb(156, 163, 175)'
                }
            },
            tooltip: {
                mode: 'index' as const,
                intersect: false,
            },
        },
        scales: {
            y: {
                beginAtZero: true,
                grid: { color: 'rgba(156, 163, 175, 0.1)' },
                ticks: {
                    font: { family: 'monospace', size: 10 },
                    color: 'rgb(156, 163, 175)'
                }
            },
            x: {
                grid: { display: false },
                ticks: {
                    font: { family: 'monospace', size: 10 },
                    color: 'rgb(156, 163, 175)'
                }
            }
        }
    };

    return (
        <div className="relative h-full w-full">
            {localResults.length === 0 && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <p className="text-xs text-text-muted font-mono opacity-60">
                        Analiza proyectos para ver tendencias
                    </p>
                </div>
            )}
            <Line data={data} options={options} />
        </div>
    );
}
